import { useEffect, useMemo, useState } from "react"
import { buildNotificationFilters, groupNotifications, parseNotification, type Notification } from "@/nostr/api/notifications"
import { useAuth } from "@/state/AuthContext"
import { useEventVersion, useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"

export interface NotificationsResult {
  notifications: Notification[]
  grouped: ReturnType<typeof groupNotifications>
  loading: boolean
}

/** Everything addressed to the signed-in user, newest first and grouped. */
export function useNotifications(): NotificationsResult {
  const { pubkey } = useAuth()
  const { pool, store } = useNostr()
  const { appRelays } = useRelays()
  const version = useEventVersion(store)
  const [loading, setLoading] = useState(Boolean(pubkey))

  useEffect(() => {
    if (!pubkey) {
      setLoading(false)
      return
    }
    setLoading(true)
    const subscription = pool.subscribe(buildNotificationFilters(pubkey), {
      relays: appRelays,
      onEvent: (event) => store.add(event),
      onEose: () => setLoading(false),
    })
    return () => subscription.close()
  }, [pubkey, pool, store, appRelays])

  return useMemo(() => {
    if (!pubkey) return { notifications: [], grouped: [], loading: false }
    const notifications = store
      .values()
      .map((event) => parseNotification(event, pubkey))
      .filter((notification): notification is Notification => Boolean(notification))
      .sort((a, b) => b.createdAt - a.createdAt)
    return { notifications, grouped: groupNotifications(notifications), loading }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pubkey, version, loading, store])
}
